import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { axPerfLogger } from '@theme/performance';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** Marqueur d'URL qui demande la réouverture du tiroir au chargement. */
  static #openParam = 'cart_drawer';

  /** Lien qui ouvre le hub Loyoly : l'app embed intercepte toute URL finissant par `#loyoly-open`. */
  static #loyaltyLinkSelector = 'a[href$="#loyoly-open"]';

  /** Tiroir du hub Loyoly, présent dans la page une fois l'app embed chargée. */
  static #loyaltyDrawerSelector = '.lds-widget-drawer';

  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {boolean} Flag to prevent re-broadcasting close events from other tabs */
  #closingFromBroadcast = false;

  /** @type {'cart'|'favorites'} Active tab — re-applied after a cart morph strips `data-tab`. */
  #currentTab = 'cart';

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('ooo-wishlist:product-added', this.#handleWishlistAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#focusDialogOnOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('click', this.#handleLoyaltyLinkClick);

    // Listen for broadcast close from other tabs
    document.addEventListener('cart:drawer-close', this.#handleBroadcastClose);

    // Auto-open drawer when landing on /cart in drawer mode with redirect enabled
    if (this.hasAttribute('data-redirect-cart-page') && window.location.pathname === `${window.Shopify?.routes?.root ?? '/'}cart`) {
      this.showDialog();
    }

    this.#syncLoginLinks();
    this.#openFromUrl();

    // Detect all dialog close events (button, Escape, click outside)
    const { dialog } = /** @type {Refs} */ (this.refs);
    dialog?.addEventListener('close', this.#handleDialogClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('ooo-wishlist:product-added', this.#handleWishlistAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#focusDialogOnOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('click', this.#handleLoyaltyLinkClick);
    document.removeEventListener('cart:drawer-close', this.#handleBroadcastClose);
    this.#historyAbortController?.abort();

    const { dialog } = /** @type {Refs} */ (this.refs);
    dialog?.removeEventListener('close', this.#handleDialogClose);
  }

  // ─── History API (back button on mobile) ──────────────────

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  // ─── Focus à l'ouverture ──────────────────────────────────

  /**
   * À l'ouverture, `dialog.showModal()` auto-focus le 1er élément focusable (lien de
   * la bannière, bouton « ajouter » d'une carte reco…) → focus-ring disgracieux.
   * showDialog() appelle showModal() dans un rAF PUIS émet DialogOpenEvent, donc ce
   * handler s'exécute APRÈS l'auto-focus et le neutralise : le focus va sur le dialog
   * lui-même (tabindex -1, sans outline ; cf. cart-drawer.css). Le focus reste piégé
   * dans la modale (a11y), sans encadrer un contrôle au hasard.
   */
  #focusDialogOnOpen = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;
    dialog.setAttribute('tabindex', '-1');
    dialog.focus({ preventScroll: true });
  };

  // ─── Onglets Panier / Favoris ─────────────────────────────

  /** Active l'onglet Panier (items + résumé). Appelé via on:click. */
  showCartTab() {
    this.#selectTab('cart');
  }

  /** Active l'onglet Favoris (placeholder ; wishlist à câbler). Appelé via on:click. */
  showFavoritesTab() {
    this.#selectTab('favorites');
  }

  /**
   * Bascule la vue Panier/Favoris : pose `data-tab` sur le composant (le CSS gère
   * l'affichage des panneaux) et synchronise l'état des onglets.
   * @param {'cart'|'favorites'} tab
   */
  #selectTab(tab) {
    this.#currentTab = tab;
    this.dataset.tab = tab;

    for (const el of this.querySelectorAll('.cart-drawer__tab')) {
      const isCartTab = el.getAttribute('aria-controls') === 'cart-drawer-panel-cart';
      const active = tab === 'cart' ? isCartTab : !isCartTab;
      el.classList.toggle('cart-drawer__tab--active', active);
      el.classList.toggle('cart-drawer__tab--inactive', !active);
      el.setAttribute('aria-selected', active ? 'true' : 'false');
    }
  }

  /**
   * A cart morph re-renders the tabs/header from server HTML (which has no
   * `data-tab` and resets the active tab to "cart"). Re-apply the user's tab.
   */
  updatedCallback() {
    super.updatedCallback?.();
    if (this.#currentTab !== 'cart') this.#selectTab(this.#currentTab);
    // Un morph réinjecte les liens de connexion depuis le HTML serveur (donc sans
    // la query string de la page) : on les recalibre.
    this.#syncLoginLinks();
  }

  // ─── Connexion depuis le panier ───────────────────────────

  /**
   * Recalibre les liens de connexion du tiroir (`[data-cart-login]`) sur l'URL
   * courante complète.
   *
   * Liquid ne sait pas lire la query string : le rendu serveur ne peut produire
   * qu'un `return_to` réduit au chemin, ce qui perdait le variant d'une fiche
   * produit ou les filtres d'une collection. Le repli sans JS s'en contente ;
   * ici on remet l'URL entière, marqueur d'ouverture compris.
   */
  #syncLoginLinks() {
    const links = this.querySelectorAll('a[data-cart-login], [data-cart-login] a');
    if (!links.length) return;

    const returnTo = new URL(window.location.href);
    returnTo.searchParams.set(CartDrawerComponent.#openParam, 'open');

    const target = `/customer_authentication/login?return_to=${encodeURIComponent(
      `${returnTo.pathname}${returnTo.search}${returnTo.hash}`
    )}`;

    for (const link of links) {
      link.setAttribute('href', target);
    }
  }

  // ─── Hub Loyoly ("Échanger mes points") ─────────────────

  /**
   * Referme le tiroir quand un de ses liens ouvre le hub Loyoly.
   *
   * Le tiroir est une modale, donc dans la couche supérieure : le hub s'ouvre
   * dessous, invisible et inerte, quel que soit son z-index. Une fois le tiroir
   * refermé, le hub apparaît.
   *
   * Sans l'app embed, le lien suit sa page de repli : on ne ferme rien, car la
   * fermeture rembobine l'historique sur mobile et gênerait la navigation.
   *
   * @param {MouseEvent} event
   */
  #handleLoyaltyLinkClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(CartDrawerComponent.#loyaltyLinkSelector)) return;
    if (!document.querySelector(CartDrawerComponent.#loyaltyDrawerSelector)) return;

    this.closeDialog();
  };

  /**
   * Rouvre le tiroir au retour de connexion, puis efface le marqueur de l'URL —
   * sinon un rechargement ou un partage du lien rouvrirait le panier sans raison.
   */
  #openFromUrl() {
    const url = new URL(window.location.href);
    if (url.searchParams.get(CartDrawerComponent.#openParam) !== 'open') return;

    url.searchParams.delete(CartDrawerComponent.#openParam);
    history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);

    if (this.refs.dialog?.open) return;
    this.open();
  }

  /**
   * Switch to (and open) the Favoris tab when a product is added to the wishlist,
   * if the merchant enabled auto-open. No-op otherwise.
   */
  #handleWishlistAdd = () => {
    if (!this.hasAttribute('auto-open-wishlist')) return;

    requestAnimationFrame(() => {
      const current = this.isConnected ? this : document.querySelector('cart-drawer-component');
      if (!(current instanceof CartDrawerComponent)) return;
      if (!current.hasAttribute('auto-open-wishlist')) return;

      current.showFavoritesTab();
      if (!current.refs?.dialog?.open) current.open();
    });
  };

  // ─── Cart Add ─────────────────────────────────────────────

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {Event & { detail?: { resource?: { item_count?: number }, data?: { source?: string, didError?: boolean } } }} event
   */
  #handleCartAdd = (event) => {
    const data = event.detail?.data;

    // Don't react on failed add-to-cart (out of stock, etc.)
    if (data?.didError) return;

    // A successful add always returns the user to the Cart tab — even if they
    // were on Favoris (e.g. "Ajouter au panier" from a favorite). Persisted in
    // #currentTab so updatedCallback re-applies it after the cart morph wipes
    // `data-tab`. Runs for every source, before the auto-open whitelist below.
    this.#selectTab('cart');

    if (!this.hasAttribute('auto-open')) return;

    // ONLY auto-open on a real user "add to cart" action — i.e. `cart:update`
    // dispatched by product-form-component (ATC click) or pdp-bundle-store
    // (bundle commit). Every other source represents passive cart state
    // syncing and should NOT pop the drawer open:
    //   - `cart-manager`        → third-party cart fetch detected by the
    //                             HTTP interceptor. The initial /cart sync
    //                             on page load was opening the drawer for
    //                             no reason.
    //   - `cart-broadcast`      → another tab opened/closed the drawer
    //   - `cart-items-component`→ qty / remove update inside the drawer
    // Whitelist user-initiated sources only — safer than blacklisting.
    if (data?.source !== 'product-form-component' && data?.source !== 'pdp-bundle-store') return;

    // Defer one frame so a concurrent section morph can replace this instance
    // before we open the dialog — redirect to the live cart-drawer if so.
    requestAnimationFrame(() => {
      const current =
        this.isConnected ? this : document.querySelector('cart-drawer-component');
      if (!(current instanceof CartDrawerComponent)) return;
      if (!current.hasAttribute('auto-open')) return;
      if (current.refs?.dialog?.open) return;
      current.open();
      current.#announceCartCount(event.detail?.resource?.item_count);
    });
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  // ─── Broadcast sync (multi-tab) ───────────────────────────

  /**
   * Dispatches a cart:drawer-close event when the dialog closes,
   * so cart-broadcast can sync the close to other tabs.
   * Skips dispatching if the close was triggered by a broadcast from another tab.
   * When on /cart with redirect enabled, navigates to home.
   */
  #handleDialogClose = () => {
    if (this.#closingFromBroadcast) {
      this.#closingFromBroadcast = false;
      return;
    }

    document.dispatchEvent(
      new CustomEvent('cart:drawer-close', { bubbles: true })
    );

    if (this.hasAttribute('data-redirect-cart-page') && window.location.pathname === `${window.Shopify?.routes?.root ?? '/'}cart`) {
      window.location.href = window.Shopify?.routes?.root ?? '/';
    }
  };

  /**
   * Handles close events received from other tabs via cart-broadcast.
   * @param {Event & { detail?: { source?: string } }} event
   */
  #handleBroadcastClose = (event) => {
    if (event.detail?.source !== 'cart-broadcast') return;

    this.#closingFromBroadcast = true;
    this.closeDialog();
  };

  // ─── Open / Close ─────────────────────────────────────────

  open() {
    const perfOp = axPerfLogger.start('drawer-open', { triggeredBy: 'manual' });
    this.showDialog();
    perfOp.mark('dialog-shown');
    this.#dispatchOpenEvent();
    perfOp.mark('open-event-dispatched');
    this.#checkLazyLoadImages();
    perfOp.mark('lazy-check-done');
    perfOp.end();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // ─── Lazy load images ─────────────────────────────────────

  /**
   * Dispatches removeLazyKeep event for images with data-lazy-keep inside the drawer,
   * so observer-CFH can load them now that the drawer is visible.
   */
  #checkLazyLoadImages() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog || dialog.querySelectorAll('[data-lazy-keep]').length === 0) return;

    document.dispatchEvent(
      new CustomEvent('removeLazyKeep', {
        detail: { lazyParentElement: dialog },
      })
    );
  }

  // ─── GTM Events ───────────────────────────────────────────

  /**
   * Dispatches a GTM-compatible event when the cart drawer opens.
   */
  #dispatchOpenEvent() {
    document.dispatchEvent(
      new CustomEvent('ax:cart-drawer-opened', {
        detail: { source: 'cart-drawer' },
        bubbles: true,
      })
    );
  }

  // ─── Sticky summary ───────────────────────────────────────

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
